import { PmStatus } from 'api/types';
import {
  MS_MULTIPLIER,
  HOUR_PX,
  APPT_DURATION_FALLBACK,
  JOIN_APPT_BEFORE_MINS,
} from 'components/ProviderHome/constants';
import { CareMemberAppointment } from 'models/careMemberAppointment';

export const composeApptBlockLayouts = (
  appointments: CareMemberAppointment[] | undefined,
  now: number
) => {
  if (!appointments?.length) return [];

  const sortedAppts = appointments.map((a) => {
    const startMs = new Date(a.meeting_date).getTime();
    const duration = a.duration ?? APPT_DURATION_FALLBACK;
    const endMs = startMs + duration * MS_MULTIPLIER;

    return {
      ...a,
      duration,
      startMs,
      endMs,
      joinable: isApptJoinable(startMs, endMs, now),
    };
  });

  const nextUpcomingIndex = sortedAppts?.findIndex(
    (a) =>
      a.startMs >= now ||
      (a.startMs <= now && a.endMs >= now && !apptDidNotOccur(a.pm_status))
  );

  // Greedily assign each appointment to the first available column (lane)
  const colEndTimes: number[] = [];
  const cols = sortedAppts?.map((appt) => {
    const end = appt.startMs + appt.duration * MS_MULTIPLIER;
    const col = colEndTimes.findIndex((t) => t <= appt.startMs);
    if (col === -1) {
      colEndTimes.push(end);
      return colEndTimes.length - 1;
    }
    colEndTimes[col] = end;
    return col;
  });

  // For each appointment, find the total number of columns in its overlap group
  const totalCols = sortedAppts?.map((appt) => {
    return sortedAppts?.reduce((max, other, j) => {
      const oEnd = other.startMs + other.duration * MS_MULTIPLIER;
      return other.startMs < appt.endMs && oEnd > appt.startMs
        ? Math.max(max, cols[j] + 1)
        : max;
    }, 1);
  });

  return sortedAppts?.map((appt, i) => {
    const apptDate = new Date(appt.meeting_date);
    const topOffset =
      apptDate.getHours() * HOUR_PX +
      (apptDate.getMinutes() * HOUR_PX) / 60 +
      HOUR_PX / 2;

    return {
      appt,
      startCol: cols[i],
      totalCols: totalCols[i],
      topOffset,
      isNext: i === nextUpcomingIndex,
    };
  });
};

/** true if appointment was rescheduled, cancelled or no show */
export const apptDidNotOccur = (
  pmStatus: PmStatus | null,
  rescheduledFor?: boolean
) => {
  const isRescheduled = !!rescheduledFor;

  const isCancelledOrNoShow = [
    PmStatus.cancelled,
    PmStatus.lateCancelled,
    PmStatus.noShow,
    PmStatus.rescheduled,
  ].includes(pmStatus as PmStatus);
  return isRescheduled || isCancelledOrNoShow;
};

export const formatDateStr = (d: Date) => {
  // returns the date as 'YYYY-MM-DD'
  return d.toLocaleDateString('en-CA');
};

export const isApptJoinable = (startMs: number, endMs: number, now: number) => {
  const fiveMinMs = JOIN_APPT_BEFORE_MINS * MS_MULTIPLIER;
  return now >= startMs - fiveMinMs && now < endMs;
};
